import { Container, Flex, Link, Text } from '@nrx/ui';
import type { JSX, ReactNode } from 'react';

import { isEmbedded } from '../embed/embedded.js';

/**
 * Brand lockup, served from the marketing CDN (same asset the vendor shell used).
 *
 * @remarks No CSP change needed: `buildCsp`'s `img-src` baseline is already
 * `'self' data: https:`.
 */
const LOGO_URL =
  'https://cdn.prod.website-files.com/619945d6e2622010be987644/6877c8800a14bc1bfe0f20bf_logoNRx.svg';

/** Footer legal links — external marketing pages, so plain anchors, not router links. */
const LEGAL_LINKS = [
  { label: 'Terms & Conditions', href: 'https://www.nourishedrx.com/terms-and-conditions' },
  { label: 'Privacy Policy', href: 'https://www.nourishedrx.com/privacy' },
] as const;

/**
 * Shared NourishedRx shell for every consult route: brand header, `<main>` content region, and a
 * footer with the copyright line and the two legal links.
 *
 * @remarks Replaces the vendor's `NrxLayout`, whose `nrx-*` Tailwind utilities were inert here
 * (the vendor's own `index.css` was never ported). Chrome geometry comes from `@nrx/ui`
 * primitives (`Container`, `Flex`, `Text`, `Link`); the two colour surfaces it needs — the cream
 * page ground and the hairline rules above and below the content — come from theme tokens in
 * `src/index.css` (W2), because no primitive carries them.
 *
 * `@nrx/ui`'s own `AppShell` is deliberately not used: it is the pantry **staff** chrome (sidebar
 * `NavTree` fed by the registry, `accessToken`, sign-out). This is an unauthenticated
 * member-facing landing shell with no navigation, so it composes primitives instead (W1).
 *
 * Inside care-betty's booking modal (`?embedded=1`) only `<main>` renders: the modal supplies its
 * own chrome, and a second brand header and footer inside it would be noise.
 *
 * `<main>` does not constrain its children — the consult pages own full-bleed hero bands and
 * their own containers.
 */
export function ConsultLayout({ children }: { children: ReactNode }): JSX.Element {
  if (isEmbedded()) {
    return <main className="consult-shell__main">{children}</main>;
  }

  return (
    <div className="consult-shell">
      <header className="consult-shell__header">
        <Container size="4" py="6">
          <img src={LOGO_URL} alt="NourishedRx" className="consult-shell__logo" />
        </Container>
      </header>

      <main className="consult-shell__main">{children}</main>

      <footer className="consult-shell__footer">
        <Container size="4" py="6">
          <Flex align="center" justify="between" wrap gap="4">
            <Text as="p" size="2" color="muted">
              NourishedRx © 2026 All Rights Reserved.
            </Text>
            <Flex as="nav" aria-label="Legal" align="center" wrap gap="5">
              {LEGAL_LINKS.map(({ label, href }) => (
                <Link
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline
                  className="consult-shell__legal-link"
                >
                  <Text size="2" color="muted">
                    {label}
                  </Text>
                </Link>
              ))}
            </Flex>
          </Flex>
        </Container>
      </footer>
    </div>
  );
}
